import {
  ChangeDetectorRef,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  HlmSelectComponent,
  HlmSelectOption,
  HLM_FORM_FIELD,
} from '../../../ui';
import { PriceList, PriceListRef } from '../brand.model';

type Mode = 'existing' | 'copy';

/**
 * Picks the `defaultPriceList` for a brand. Two modes:
 *   - `existing`: reuse a price list already in the catalogue → emits `{ id }`.
 *   - `copy`:     clone an existing list under a new name → emits `{ name, copyFromId }`.
 *
 * Implements `ControlValueAccessor` so it can plug into a parent `FormGroup`.
 *
 * TODO(EB-3638): the `copy` mode UI is hidden in the template until the
 * backend's `POST /contract/v2/rest/pricelists/{id}/clone` endpoint is fixed.
 * Current bug: PriceListService.clonePriceList requires the new-name row to
 * already exist in TARIFSTRUKTUR (it never calls createNewPriceList), so the
 * API always 500s with "Price list with name X doesn't exist". The TS here
 * still supports the clone shape — restoring the radios is a template-only
 * change once backend lands the fix.
 */
@Component({
  selector: 'cmt-price-list-picker',
  standalone: true,
  // NB: Radios + text input are intentionally excluded from imports while the
  // copy-mode UI is hidden (see TODO above). Re-add HlmRadioGroupComponent,
  // HlmRadioComponent, HlmInputDirective when the template is restored.
  imports: [
    FormsModule,
    HlmSelectComponent,
    ...HLM_FORM_FIELD,
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PriceListPickerComponent), multi: true },
  ],
  templateUrl: './price-list-picker.component.html',
})
export class PriceListPickerComponent implements ControlValueAccessor {
  readonly priceLists = input<readonly PriceList[]>([]);
  readonly disabled = signal(false);

  protected readonly mode = signal<Mode>('existing');
  protected readonly existingId = signal<number | null>(null);
  protected readonly copyFromId = signal<number | null>(null);
  protected readonly copyName = signal<string>('');

  protected readonly options = computed<HlmSelectOption<number>[]>(() =>
    this.priceLists().map(pl => ({ value: pl.id, label: `${pl.name} (id ${pl.id})` })),
  );

  private readonly cdr = inject(ChangeDetectorRef);
  private onChange: (v: PriceListRef | null) => void = () => { /* CVA */ };
  onTouched: () => void = () => { /* CVA */ };

  /**
   * Push the current value to the parent form. Called ONLY from the user-event
   * handlers below — never from `writeValue`. Emitting on a `writeValue`-driven
   * signal change (the old constructor `effect` did this) echoed a fresh
   * `{ id }` object back into the control on hydration, which marked the brand
   * form dirty without any user edit (the parent's dirty check compares the
   * price-list by reference). See brand-form `dirty` (compares by id).
   */
  private emit(): void {
    this.onChange(this.serialize());
    this.onTouched();
  }

  private serialize(): PriceListRef | null {
    if (this.mode() === 'existing') {
      const id = this.existingId();
      return id === null ? null : { id };
    }
    const name = this.copyName().trim();
    const copyFromId = this.copyFromId();
    if (!name || copyFromId === null) return null;
    return { name, copyFromId };
  }

  protected setMode(next: Mode): void {
    if (this.disabled()) return;
    this.mode.set(next);
    this.emit();
  }

  protected onExistingChange(id: number | null): void {
    this.existingId.set(id);
    this.emit();
  }

  protected onCopyFromChange(id: number | null): void {
    this.copyFromId.set(id);
    this.emit();
  }

  protected onCopyNameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.copyName.set(target.value);
    this.emit();
  }

  // ControlValueAccessor

  writeValue(value: PriceListRef | null): void {
    if (!value) {
      this.mode.set('existing');
      this.existingId.set(null);
      this.copyFromId.set(null);
      this.copyName.set('');
    } else if ('id' in value) {
      this.mode.set('existing');
      this.existingId.set(value.id);
      this.copyFromId.set(null);
      this.copyName.set('');
    } else {
      this.mode.set('copy');
      this.existingId.set(null);
      this.copyFromId.set(value.copyFromId);
      this.copyName.set(value.name);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: PriceListRef | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled.set(d); }
}
